import {Button, useMediaQuery} from '@material-ui/core';
import 'Agent/AgentForms/Marketing/components/Marketing.sass';
import MarketingDocuments from 'Agent/AgentForms/Marketing/components/MarketingDocuments';
import MarketingFormQuantity from 'Agent/AgentForms/Marketing/components/MarketingFormQuantity';
import MarketingSearchForm from 'Agent/AgentForms/Marketing/components/MarketingSearchForm';
import {
  changeCartVisibility,
  changeMarketingFilters,
  changeSort,
  clearMarketing,
  clearSort,
  clearViewCart,
  DEFAULT_FORM_NUMBER,
  getMarketing,
  removeCartItems,
  StateFilter,
  changeCheckoutCart,
  clearCheckoutCart,
  incrementedAmount,
} from 'Agent/AgentForms/Marketing/ducks/Marketing';
import * as MarketingStatesAction from 'Agent/AgentForms/Marketing/ducks/marketingFormState';
import {Marketing, MarketingFormStateDto} from 'Agent/AgentForms/Marketing/models/Marketing';
import {ModalState, showModal} from 'Core/ducks/modal';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import * as O from 'fp-ts/lib/Option';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import {bindActionCreators} from 'redux';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {withSelectFilterProperties, withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {ActionsOf} from 'Shared/utilities/actionCreator';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import './Marketing.sass';
import MarketingViewCartForm from './MarketingViewCart';
import {ShoppingCartQuantities} from 'Shared/constants/ShoppingCart';

type MarketingProps = {
  selectedAgency: AgencyFilters;
} & RouteComponentProps;

const MarketingForm = (props: MarketingProps) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const dispatch = useDispatch();
  useBreadcrumbs('Marketing Forms', props.match?.url);
  const {selectedAgency} = props;
  const {
    marketingFormsType,
    marketingFormsCategories,
    marketing,
    filters,
    sort,
    viewCart,
    inCartVisible,
    checkoutCart,
  } = useSelector((state: ReducerState) => state.marketing);
  const {getMarketingFormStates} = bindActionCreators(
    MarketingStatesAction as ActionsOf<typeof MarketingStatesAction>,
    dispatch
  );
  const [isSearchBlock, setIsSearchBlock] = useState(false);
  const [isViewCartBlock, setIsViewCartBlock] = useState(false);
  const [cart, setCart] = useState(viewCart);
  const [cartFinal, setCartFinal] = useState(viewCart);
  const [visible, setVisible] = useState(inCartVisible);

  const onViewStates = (marketingFormStateDto: MarketingFormStateDto) => () => {
    getMarketingFormStates(marketingFormStateDto);
    dispatch(showModal(ModalState.MarketingFormStates({states: marketingFormStateDto})));
  };

  const changeFilter = withValueChangedHook(filters, changeMarketingFilters, dispatch);

  const filterProperties = {
    changeFilter,
    filterData: filters,
  };

  useEffect(() => {
    dispatch(changeMarketingFilters({...filters}));
  }, []);

  useEffect(() => {
    changeCartVisibility({...visible});
  }, [visible]);

  useEffect(() => {
    filters.pageNumber = '1';
    dispatch(getMarketing(undefined, filters, O.toUndefined(sort), selectedAgency.isNewYork));
  }, [filters, sort, selectedAgency.isNewYork]);

  useEffect(() => {
    return () => {
      removeCartItems(inCartVisible, cart, setCart);
      dispatch(changeCartVisibility({...inCartVisible}));
      dispatch(clearViewCart());
      dispatch(clearMarketing({...filters}));
      dispatch(clearSort());
      checkoutCart?.items?.clear();
      dispatch(clearCheckoutCart());
    };
  }, [clearMarketing, changeMarketingFilters]);

  const onClickSearchForm = () => {
    setIsSearchBlock(!isSearchBlock);
  };

  const onClickViewCart = () => {
    setIsViewCartBlock(!isViewCartBlock);
  };

  function onClickAddToCart(marketingItem: Marketing) {
    const currentFormNumber =
      marketingItem && marketingItem.formNumber ? marketingItem.formNumber : DEFAULT_FORM_NUMBER;
    let checkoutCurrentItem = checkoutCart.items.get(currentFormNumber);
    let formQuantity = checkoutCurrentItem === undefined || checkoutCurrentItem === 0 ? 1 : checkoutCurrentItem;
    let formQuantityIncrement = marketingItem.unitIncrements
      ? marketingItem.unitIncrements
      : ShoppingCartQuantities.Increment;
    let formQuantityMin = marketingItem.minQuantity ? marketingItem.minQuantity : ShoppingCartQuantities.Min;
    if (formQuantity < formQuantityMin) formQuantity = formQuantityMin;
    visible.inCartFormNumber.push(currentFormNumber);
    cart.items.set(currentFormNumber, {
      ...marketingItem,
      quantity: incrementedAmount(formQuantity, formQuantityIncrement),
    });
    setVisible({...visible});
    setCartFinal({...cart});
    if (checkoutCurrentItem !== undefined) {
      checkoutCart.items.delete(currentFormNumber);
      dispatch(changeCheckoutCart({...checkoutCart}));
    }
  }

  return (
    <>
      <div
        className="backdrop-container"
        id="backdrop"
        style={{display: isSearchBlock || isViewCartBlock ? 'block' : 'none'}}
      />
      <PagedView
        pagedViewKey="marketing"
        sort={O.toUndefined(sort)}
        titleOrType={filters?.titleOrType}
        onChangeSort={sort => dispatch(changeSort(sort))}
        loadMore={page => {
          const nextPage: number = parseInt(filters.pageNumber) + 1;
          if (page.totalCount > page.result.length) {
            filters.pageNumber = nextPage.toString();
            let sortValue =
              sort._tag === 'Some' ? '&sortColumn=' + sort.value.column + '&sortOrder=' + sort.value.direction : '';
            let link;
            if (sortValue !== '') {
              link = addFilterParameters(filters) + '&isNewYork=' + selectedAgency.isNewYork + sortValue;
            } else {
              link = addFilterParameters(filters) + '&isNewYork=' + selectedAgency.isNewYork;
            }
            page.links = [
              {
                href: 'api/documents/v1/marketingForms' + link,
                method: 'GET',
                rel: 'nextpage',
              },
            ];
          }
          dispatch(getMarketing(page, filters, O.toUndefined(sort), selectedAgency.isNewYork));
        }}
        actions={
          <>
            <div className={isDesktop ? 'action-buttons-desktop' : 'action-buttons-mobile'}>
              <Button
                id="onClickSearchFormBtn"
                color="primary"
                variant="contained"
                style={{margin: isDesktop ? '0 1rem 0 0' : '0'}}
                onClick={onClickSearchForm}
              >
                {' '}
                SEARCH FORMS
              </Button>
              <Button
                id="onClickViewCartBtn"
                color="primary"
                variant="contained"
                onClick={onClickViewCart}
                disabled={cart.items.size <= 0}
              >
                {' '}
                {cart.items.size == 0 ? `VIEW CART` : `VIEW CART (${cart.items.size})`}
              </Button>
            </div>
          </>
        }
        data={marketing}
      >
        {columnProps => {
          return (
            <>
              <Column
                {...columnProps}
                title="States"
                sortColumnKey="states"
                filterProperties={withSelectFilterProperties(filterProperties, 'states', StateFilter)}
                default
              >
                {marketing => (
                  <Button
                    id={`marketing-view-states-btn-${marketing.fId}`}
                    className="primary-action-button"
                    color="primary"
                    onClick={onViewStates({fid: marketing.fId})}
                  >
                    View
                  </Button>
                )}
              </Column>
              <Column
                {...columnProps}
                title="Category"
                sortColumnKey="category"
                filterProperties={withSelectFilterProperties(filterProperties, 'category', marketingFormsCategories)}
                default
              >
                {marketing => marketing.category}
              </Column>
              <Column
                {...columnProps}
                title="Type"
                sortColumnKey="type"
                filterProperties={withSelectFilterProperties(filterProperties, 'type', marketingFormsType)}
                default
              >
                {marketing => marketing.type}
              </Column>
              <Column
                {...columnProps}
                title="Title"
                sortColumnKey="title"
                filterProperties={withStringFilterProperties(filterProperties, 'title')}
                default
              >
                {marketing => marketing.title}
              </Column>
              <Column
                {...columnProps}
                title="Form Number"
                sortColumnKey="formNumber"
                filterProperties={withStringFilterProperties(filterProperties, 'formNumber')}
                default
              >
                {marketing => marketing.formNumber}
              </Column>
              <Column {...columnProps} title="Link" default>
                {marketing => <MarketingDocuments marketing={marketing} selectedAgency={selectedAgency} />}
              </Column>
              <Column
                {...columnProps}
                title="Description"
                sortColumnKey="description"
                filterProperties={withStringFilterProperties(filterProperties, 'description')}
                default
              >
                {marketing => marketing.description}
              </Column>
              <Column {...columnProps} title="Quantity" default>
                {marketing =>
                  marketing.onlineOnly !== '1' ? (
                    <MarketingFormQuantity marketing={marketing} cartData={cart} checkoutItems={checkoutCart} />
                  ) : (
                    <div />
                  )
                }
              </Column>
              <Column {...columnProps} title="" isActions default>
                {marketing =>
                  marketing.onlineOnly !== '1' ? (
                    <Button
                      id={`marketing-addtocart-btn-${marketing.fId}`}
                      className="primary-action-button"
                      color="primary"
                      //style={{backgroundColor: '#0033A0', color: '#fff'}}
                      disabled={
                        marketing && marketing.formNumber
                          ? visible.inCartFormNumber.includes(marketing.formNumber)
                          : visible.inCartFormNumber.includes(DEFAULT_FORM_NUMBER)
                      }
                      onClick={() => {
                        onClickAddToCart(marketing);
                      }}
                    >
                      ADD TO CART
                    </Button>
                  ) : (
                    <Button
                      id={`marketing-addtocart-btn-${marketing.fId}`}
                      className="primary-action-button"
                      color="primary"
                      disabled={true}
                    >
                      ONLINE ONLY
                    </Button>
                  )
                }
              </Column>
            </>
          );
        }}
      </PagedView>
      {isSearchBlock && (
        <MarketingSearchForm
          formName="Marketing Forms"
          setIsSearchBlock={setIsSearchBlock}
          filters={filters}
          filter={changeMarketingFilters}
          marketingCategories={marketingFormsCategories}
          marketingTypes={marketingFormsType}
        />
      )}
      {isViewCartBlock && <MarketingViewCartForm setIsViewCartBlock={setIsViewCartBlock} viewCartDetails={viewCart} />}
    </>
  );
};

export default MarketingForm;
